import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@pavanhiro/common-ui';

interface PricingTier {
  id: string;
  name: string;
  price: string;
  description: string;
  features: string[];
  cta: string;
  ctaVariant: 'outline' | 'primary' | 'secondary';
  popular?: boolean;
  mau: string;
}

const pricingTiers: PricingTier[] = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    mau: '10,000 MAU',
    description: 'Perfect for side projects and MVP testing',
    features: [
      '10,000 monthly active users',
      'OAuth 2.0 & OIDC flows',
      'Social logins (Google, GitHub)',
      'MFA (TOTP, SMS)',
      'Email/password authentication',
      'Community support',
      'Standard SLA (99.5%)',
    ],
    cta: 'Start Free',
    ctaVariant: 'outline',
  },
  {
    id: 'business',
    name: 'Business',
    price: '$299',
    mau: '100,000 MAU',
    description: 'For growing startups and production apps',
    features: [
      '100,000 monthly active users',
      'Everything in Free, plus:',
      'SAML 2.0 SSO',
      'Advanced MFA (WebAuthn, biometric)',
      'Custom branding & domains',
      'Role-based access control (RBAC)',
      'Audit logs & compliance',
      'Priority email support',
      'Standard SLA (99.9%)',
      '$2 per 1,000 additional MAU',
    ],
    cta: 'Start 14-day trial',
    ctaVariant: 'primary',
    popular: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    mau: 'Unlimited MAU',
    description: 'For large organizations with compliance needs',
    features: [
      'Unlimited monthly active users',
      'Everything in Business, plus:',
      'WS-Federation support',
      'SCIM 2.0 user provisioning',
      'Advanced threat protection',
      'Custom integrations & webhooks',
      'Dedicated infrastructure',
      '99.99% SLA with credits',
      'White-glove onboarding',
      '24/7 phone & Slack support',
      'Annual security reviews',
      'Custom contract terms',
    ],
    cta: 'Contact Sales',
    ctaVariant: 'secondary',
  },
];

export default function PricingPage() {
  const navigate = useNavigate();

  const handleSelectPlan = (planId: string) => {
    if (planId === 'enterprise') {
      // Enterprise requires consultation
      window.location.href = 'mailto:sales@zoeiq.com?subject=Enterprise Plan Inquiry';
    } else {
      // Navigate to signup with plan parameter
      navigate(`/signup?plan=${planId}`);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px',
    }}>
      {/* Header */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center', marginBottom: '48px' }}>
        <Link to="/" style={{ 
          fontSize: '24px', 
          fontWeight: 700, 
          color: '#fff', 
          textDecoration: 'none',
          display: 'inline-block',
          marginBottom: '24px',
        }}>
          ZoAuth
        </Link>
        <h1 style={{ 
          fontSize: '42px', 
          fontWeight: 800, 
          color: '#fff', 
          marginBottom: '16px',
          lineHeight: 1.2,
        }}>
          Simple, transparent pricing
        </h1>
        <p style={{ 
          fontSize: '18px', 
          color: 'rgba(255,255,255,0.9)', 
          maxWidth: '600px', 
          margin: '0 auto',
          lineHeight: 1.6,
        }}>
          Start free, scale as you grow. No hidden fees, cancel anytime.
        </p>

        {/* Billing toggle (future enhancement) */}
        <div style={{ marginTop: '32px', color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
          💡 All plans include 14-day free trial • No credit card required for Free tier
        </div>
      </div>

      {/* Pricing Cards */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '32px',
        alignItems: 'stretch',
      }}>
        {pricingTiers.map((tier) => (
          <div
            key={tier.id}
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '32px',
              boxShadow: tier.popular 
                ? '0 20px 60px rgba(0,0,0,0.25)' 
                : '0 8px 30px rgba(0,0,0,0.12)',
              position: 'relative',
              border: tier.popular ? '3px solid #667eea' : '1px solid #e2e8f0',
              transform: tier.popular ? 'scale(1.05)' : 'scale(1)',
              transition: 'transform 0.2s',
            }}
          >
            {tier.popular && (
              <div style={{
                position: 'absolute',
                top: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                padding: '6px 20px',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                Most Popular
              </div>
            )}

            {/* Plan Name */}
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ 
                fontSize: '24px', 
                fontWeight: 700, 
                color: '#1a202c',
                marginBottom: '8px',
              }}>
                {tier.name}
              </h3>
              <p style={{ 
                fontSize: '14px', 
                color: '#64748b',
                lineHeight: 1.5,
              }}>
                {tier.description}
              </p>
            </div>

            {/* Price */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                <span style={{ 
                  fontSize: '48px', 
                  fontWeight: 800, 
                  color: '#1a202c',
                  lineHeight: 1,
                }}>
                  {tier.price}
                </span>
                {tier.price !== 'Custom' && (
                  <span style={{ fontSize: '16px', color: '#64748b' }}>
                    /month
                  </span>
                )}
              </div>
              <div style={{ 
                fontSize: '14px', 
                color: '#667eea', 
                fontWeight: 600,
                marginTop: '8px',
              }}>
                {tier.mau}
              </div>
            </div>

            {/* CTA Button */}
            <Button
              variant={tier.ctaVariant}
              size="large"
              onClick={() => handleSelectPlan(tier.id)}
              style={{ width: '100%', marginBottom: '24px' }}
            >
              {tier.cta}
            </Button>

            {/* Features */}
            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '24px' }}>
              <ul style={{ 
                listStyle: 'none', 
                padding: 0, 
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}>
                {tier.features.map((feature, idx) => (
                  <li key={idx} style={{ 
                    display: 'flex', 
                    gap: '12px',
                    fontSize: '14px',
                    color: feature.includes('Everything in') ? '#667eea' : '#475569',
                    fontWeight: feature.includes('Everything in') ? 600 : 400,
                  }}>
                    <span style={{ 
                      color: '#48bb78', 
                      fontSize: '16px',
                      flexShrink: 0,
                    }}>
                      ✓
                    </span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* FAQ / Features Comparison */}
      <div style={{
        maxWidth: '900px',
        margin: '80px auto 0',
        background: 'rgba(255,255,255,0.95)',
        borderRadius: '16px',
        padding: '40px',
      }}>
        <h2 style={{ 
          fontSize: '28px', 
          fontWeight: 700, 
          color: '#1a202c',
          marginBottom: '24px',
          textAlign: 'center',
        }}>
          All plans include
        </h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '24px',
          marginTop: '32px',
        }}>
          {[
            { icon: '🔒', title: 'SOC 2 Type II Certified', desc: 'Enterprise-grade security' },
            { icon: '🌍', title: 'Multi-region deployment', desc: 'Global edge network' },
            { icon: '⚡', title: '< 100ms latency', desc: 'Lightning-fast auth flows' },
            { icon: '📊', title: 'Real-time analytics', desc: 'Monitor your auth metrics' },
            { icon: '🔄', title: '99.9%+ uptime', desc: 'Reliable authentication' },
            { icon: '📚', title: 'Comprehensive docs', desc: 'Guides & code samples' },
          ].map((item, idx) => (
            <div key={idx} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>{item.icon}</div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: '#1a202c', marginBottom: '4px' }}>
                {item.title}
              </div>
              <div style={{ fontSize: '13px', color: '#64748b' }}>
                {item.desc}
              </div>
            </div>
          ))}
        </div>

        <div style={{ 
          marginTop: '40px', 
          textAlign: 'center',
          fontSize: '14px',
          color: '#64748b',
        }}>
          <p>Questions about pricing or features?</p>
          <a 
            href="mailto:sales@zoeiq.com" 
            style={{ 
              color: '#667eea', 
              textDecoration: 'none', 
              fontWeight: 600,
            }}
          >
            Contact our sales team →
          </a>
        </div>
      </div>

      {/* Footer */}
      <div style={{ 
        maxWidth: '1200px', 
        margin: '60px auto 0', 
        textAlign: 'center',
        color: 'rgba(255,255,255,0.7)',
        fontSize: '13px',
      }}>
        <div style={{ marginBottom: '12px' }}>
          <Link to="/login" style={{ color: 'rgba(255,255,255,0.9)', marginRight: '24px' }}>
            Sign In
          </Link>
          <Link to="/signup" style={{ color: 'rgba(255,255,255,0.9)', marginRight: '24px' }}>
            Sign Up
          </Link>
          <a href="https://docs.zoeiq.com" style={{ color: 'rgba(255,255,255,0.9)' }}>
            Documentation
          </a>
        </div>
        <div>© 2026 ZOEIQ Labs. All rights reserved.</div>
      </div>
    </div>
  );
}
